import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Helmet from 'react-helmet';
import {
  Badge,
  Box,
  Container,
  Heading,
  Image,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { fetchLogbookEntries } from '../utils/logbookApi';

const FALLBACK_COLORS = [
  '#ffcc00',
  '#3eb297',
  '#36a9e1',
  '#e95995',
  '#3b235a',
  '#e73341',
];

const ACTIVITY_CARD_LABELS = {
  evento: 'Evento',
  laboratorio: 'Laboratorio',
  gioco_libero: 'Gioco libero',
  torneo: 'Torneo',
  centro_estivo: 'Centro estivo',
  riunione: 'Riunione',
  altro: 'Altro',
};

const formatDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/** Chiavi React stabili per tag (anche duplicati), senza usare l’indice dell’array. */
const tagsWithStableKeys = (slug, tags) => {
  const list = Array.isArray(tags) ? tags : [];
  const seen = new Map();
  return list.map((raw) => {
    const label = String(raw);
    const n = (seen.get(label) || 0) + 1;
    seen.set(label, n);
    return {
      reactKey: `${slug || 'logbook'}:${label}:${n}`,
      label,
    };
  });
};

const LogbookCard = ({ entry, index }) => {
  const colorA = FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  const colorB = FALLBACK_COLORS[(index + 2) % FALLBACK_COLORS.length];
  const image =
    entry.heroImage?.formats?.medium?.url || entry.heroImage?.url || null;

  const dateLabel = formatDate(entry.date);

  return (
    <Box
      as={Link}
      to={`/logbook/${entry.slug}`}
      display="block"
      bg="white"
      borderRadius="xl"
      overflow="hidden"
      boxShadow="md"
      border="1px solid"
      borderColor="gray.100"
      cursor="pointer"
      transition="box-shadow 0.2s ease, transform 0.2s ease"
      _hover={{ boxShadow: 'lg', transform: 'translateY(-2px)' }}
      _focusVisible={{ outline: '2px solid #36a9e1', outlineOffset: '3px' }}
      role="group"
      textDecoration="none"
      color="inherit"
    >
      <Box position="relative" w="100%" h="220px">
        {image ? (
          <Image
            src={image}
            alt={entry.heroImage?.alt || entry.title}
            w="100%"
            h="100%"
            objectFit="cover"
            display="block"
          />
        ) : (
          <Box
            w="100%"
            h="100%"
            bgGradient={`linear(to-r, ${colorA}, ${colorB})`}
          />
        )}
        <Box
          position="absolute"
          inset={0}
          bgGradient="linear(to-t, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.35) 45%, transparent 100%)"
          pointerEvents="none"
        />
        <Box
          position="absolute"
          left={0}
          right={0}
          bottom={0}
          zIndex={1}
          px={4}
          pb={4}
          pt={10}
        >
          {dateLabel ? (
            <Text
              fontSize="xs"
              color="whiteAlpha.900"
              textTransform="uppercase"
              letterSpacing="wider"
              mb={1}
            >
              {dateLabel}
            </Text>
          ) : null}
          <Heading
            as="h2"
            color="white"
            fontSize={{ base: 'md', sm: 'lg' }}
            lineHeight="short"
            textShadow="0 2px 12px rgba(0,0,0,0.45)"
            noOfLines={3}
          >
            {entry.title}
          </Heading>
        </Box>
      </Box>

      <Stack spacing={3} p={5}>
        {entry.activity_type && ACTIVITY_CARD_LABELS[entry.activity_type] ? (
          <Badge
            alignSelf="flex-start"
            variant="outline"
            colorScheme="purple"
            fontSize="xs"
          >
            {ACTIVITY_CARD_LABELS[entry.activity_type]}
          </Badge>
        ) : null}
        <Text color="gray.700" noOfLines={3}>
          {entry.excerpt}
        </Text>
        <Stack direction="row" wrap="wrap">
          {tagsWithStableKeys(entry.slug, entry.tags).map(
            ({ reactKey, label }) => (
              <Badge
                key={reactKey}
                bg="#36a9e1"
                color="white"
                borderRadius="full"
                px={2}
              >
                {label}
              </Badge>
            )
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

const Logbook = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const response = await fetchLogbookEntries({ page: 1, limit: 24 });
        if (!active) return;
        setEntries(response.entries || []);
        setError('');
      } catch (err) {
        if (!active) return;
        setEntries([]);
        setError('Non è stato possibile caricare il diario.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <Box bg="#f7fafc" minH="100vh" py={{ base: 6, md: 10 }}>
      <Helmet>
        <title>Diario di Bordo - CIL</title>
        <meta
          name="description"
          content="Aggiornamenti, racconti, foto e momenti dalle attività di CIL."
        />
        <meta property="og:title" content="Diario di Bordo - CIL" />
        <meta
          property="og:description"
          content="Aggiornamenti, racconti, foto e momenti dalle attività di CIL."
        />
      </Helmet>
      <Container maxW="container.xl">
        <Stack spacing={5}>
          <Heading color="#3b235a" size="xl">
            Diario di Bordo
          </Heading>
          <Text color="gray.700">
            Aggiornamenti, racconti, foto e momenti dalle attività di CIL.
          </Text>
        </Stack>

        {loading ? (
          <Box py={16} textAlign="center">
            <Spinner />
          </Box>
        ) : null}

        {!loading && error ? (
          <Box py={8}>
            <Text color="red.500">{error}</Text>
          </Box>
        ) : null}

        {!loading && !error && entries.length === 0 ? (
          <Box py={16}>
            <Text color="gray.600">
              Non ci sono ancora aggiornamenti pubblicati.
            </Text>
          </Box>
        ) : null}

        {!loading && !error && entries.length > 0 ? (
          <SimpleGrid mt={8} columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
            {entries.map((entry, index) => (
              <LogbookCard key={entry.slug} entry={entry} index={index} />
            ))}
          </SimpleGrid>
        ) : null}
      </Container>
    </Box>
  );
};

export default Logbook;
